import { useState, useEffect, Fragment, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import PropTypes from 'prop-types'
import { useNavigate } from 'react-router-dom'
import Autocomplete from '@mui/material/Autocomplete'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Box from '@mui/material/Box'
import Alert from '@mui/material/Alert'
import Type from 'components/Type'
import Button from 'components/Button'
import TextButton from 'components/TextButton'
import Icon from 'components/Icon'
import Img from 'components/Img'
import A from 'components/A'
import Currency from 'components/Currency'
import InlineButton from 'components/CommonUI/InlineButton'
import CircleFrame from 'components/CommonUI/CircleFrame'
import CircleButton from 'components/CircleButton'
import Divider from 'components/Divider'
import Chip from 'components/Chip'
import Input from 'components/Input'
import IconButton from 'components/IconButton'
import Paper from 'components/Paper'
import Label from 'components/Label'
import IconAction from 'components/IconAction'
import DatePicker from 'components/DatePicker'
import DateRangePicker from 'components/DateRangePicker'
import StateSelect from 'components/CommonUI/StateSelect'
import ModalFlow from 'components/ModalFlow'
import DestinationsNotCovered from 'components/ModalViews/DestinationsNotCovered'
import { usePolicy, useAction } from 'utils/actions'
import {
  GET_INSURANCE_PRODUCTS_DESTINATION_COUNTRIES,
} from 'api/constants'
import moment from 'moment/moment'

const currentDate = new Date()
const tomorrow = new Date()
tomorrow.setDate(currentDate.getDate())
const futureDate = new Date()
futureDate.setDate(tomorrow.getDate() + 180)

const shouldDisableDate = date => date > futureDate || date < currentDate

export const EXCLUDED_DESTINATIONS = [
  'Afghanistan (exc. Wakhan Corridor)',
  'Belarus',
  'Burkina Faso',
  'Central African Republic',
  'Chad',
  'Crimea',
  'Cuba',
  'Donetsk',
  'Haiti',
  'Iran, Islamic Republic of',
  'Iraq',
  `Korea, Democratic People's Republic of`,
  'Libya',
  'Luhansk',
  'Mali',
  'Mauritania',
  'Myanmar',
  'Niger',
  'North Korea',
  'Russian Federation',
  'Somalia',
  'South Sudan',
  'Sudan',
  'Syrian Arab Republic',
  'Ukraine',
  'Venezuela, Bolivarian Republic of',
  'Yemen',
]

const DestinationCountries = () => {
  const [value, setValue] = useState(null)
  const [inputValue, setInputValue] = useState('')
  const insuranceProductDestinationCountries = useSelector(({ global }) => global.insuranceProductDestinationCountries) || []
  const {
    policy,
    addDestinationCountry,
    removeDestinationCountry,
  } = usePolicy()
  const {
    destinations,
  } = policy

  return (
    <Stack
      gap={2}
    >
      <Stack
        display="grid"
        gridTemplateColumns="1fr"
        gap={1}
        width="100%"
      >
        <Autocomplete
          className="outlined"
          getOptionLabel={option => typeof option === 'string' ? option : [option.name].filter(item => item?.length).join(' ')}
          options={insuranceProductDestinationCountries}
          renderOption={(props, option) => (
            <div {...props}>
              <Type
                variant="dataSmall"
              >
                {option?.name || 'Not found'}
              </Type>
            </div>
          )}
          noOptionsText={null}
          autoComplete
          fullWidth
          value={value}
          onChange={(event, newValue) => {
            setValue('')
            setInputValue('')
            if (!!newValue) addDestinationCountry(newValue)
          }}
          onInputChange={(event, newInputValue) => {
            setInputValue(newInputValue)
          }}
          renderInput={params => (
            <TextField
              {...params}
              variant="filled"
              InputProps={{
                ...params.InputProps,
                type: 'search',
              }}
            />
          )}
        />
      </Stack>
      <Stack
        direction="row"
        flexWrap="wrap"
        gap={1.5}
        justifyContent="flex-start"
        alignContent="flex-start"
      >
        {destinations.map((o, idx) => (
          <Chip
            key={`destination_${idx}`}
            duoTone
            borderWidth="main"
            font="bodySmall"
            variant="primary_b500"
            onClose={() => removeDestinationCountry(idx)}
          >
            {o.name}
          </Chip>
        ))}
      </Stack>
    </Stack>
  )
}

const STEPS = {
  TRIP_PROTECTION: 0,
  APPLICANT_INFO: 1,
  RESIDENCE: 2,
  TRAVELER_INFO: 3,
  DESTINATION: 4,
  FIRST_PAYMENT: 5,
  TRAVEL_DATES: 6,
  NON_COVERED_DESTINATIONS: 7,
}

const AddTripProtection = ({
  vendorID,
  productID,
  isMulti,
  tripID,
}) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { closeModal } = useAction()
  const [activeIndex, setActiveIndex] = useState(productID ? STEPS.APPLICANT_INFO : STEPS.TRIP_PROTECTION)
  const {
    insuranceProducts,
    policy,
    setPolicy,
    clearPolicy,
    addTraveler,
    setTraveler,
    removeTraveler,
  } = usePolicy()
  const {
    vendor_id,
    self,
    travelers,
    destinations,
    start_date,
    end_date,
    country,
    state,
    is_multi,
    deposit_date,
  } = policy

  const multiDateError = useMemo(() => {
    const days = state === 'FL' ? 30 : 90
    return (!isMulti && !is_multi) && start_date && end_date && moment(end_date) > moment(start_date).add(days - 1, 'days') ? `Trip date range must be ${days} days or fewer` : null
  }, [start_date, end_date, isMulti, is_multi, state])

  useEffect(() => {
    if (productID) {
      clearPolicy()
      setPolicy({ 
        is_multi: isMulti,
        product_id: productID,
        vendor_id: vendorID,
        trip_id: tripID,
      })
    }
  }, [])

  useEffect(() => {
    if ((vendorID || vendor_id) && (!isMulti && !is_multi)) {
      dispatch({
        type: GET_INSURANCE_PRODUCTS_DESTINATION_COUNTRIES,
        productID: vendorID || vendor_id,
      })
    }
  }, [vendorID, vendor_id, country, isMulti, is_multi])
  
  // useEffect(() => {
  //   if ((vendorID || vendor_id) && country?.length) {
  //     dispatch({
  //       type: GET_INSURANCE_PRODUCT_STATES,
  //       productID: vendorID || vendor_id,
  //       countryID: country,
  //     })
  //   }
  // }, [vendorID, vendor_id, country, isMulti, is_multi])

  useEffect(() => {
    if (activeIndex === 0) {
      clearPolicy()
      setPolicy({
        trip_id: tripID,
      })
    }
  }, [activeIndex])

  return (
    <ModalFlow
      activeIndex={activeIndex}
      setActiveIndex={setActiveIndex}
      sizeX="xl"
      sx={{
        minHeight: '400px',
        justifyContent: 'center',
      }}
    >
      {/* 0. TRIP PROTECTION */}
      <Stack
        p={3}
        textAlign="center"
      >
        <Label
          variant="labelSmallTertiaryUpperBold"
          color="primaryHue"
          mb={2}
        >
          Trip Protection
        </Label>
        <Type
          variant="nameplateSecondary"
          mb={5}
        >
          Which type of trip protection are you looking for?
        </Type>
        <Stack
          display="grid"
          gap={3}
          gridTemplateColumns={{
            sm: 'repeat(2, 1fr)',
          }}
        >
          {insuranceProducts.map(o => (
            <Paper
              key={o.unique_id}
              mixin="paperButton"
              onClick={() => {
                setPolicy({ 
                  is_multi: !o.is_single_trip,
                  product_id: o.unique_id,
                  vendor_id: o.vendor_id,
                })
                setActiveIndex(STEPS.APPLICANT_INFO)
              }}
            >
              <Stack
                p={2}
                display="inline-grid"
                justifyItems="center"
                textAlign="center"
                width="100%"
              >
                <CircleFrame
                  mb={1.5}
                >
                  <Box
                    width="45px"
                    height="60px"
                  >
                    <Img
                      src={o.icon_url}
                      growY
                      noEffect
                    />
                  </Box>
                </CircleFrame>
                <Type
                  variant="data"
                  mb={.5}
                >
                  {o.title}
                </Type>
                <Type
                  variant="dataSmall"
                  color="neutralCool_700"
                >
                  Plans starting at <strong><Currency amount={o.price} /> /{o.price_per}</strong>
                </Type>
              </Stack>
            </Paper>
          ))}
        </Stack>
      </Stack>

      {/* 1. APPLICANT INFO */}
      <Stack
        p={3}
        textAlign="center"
      >
        <Label
          color="primaryHue"
          mb={2}
        >
          Applicant Info
        </Label>
        <Type
          variant="nameplateSecondary"
          mb={3}
        >
          Are you purchasing trip protection for yourself?
        </Type>
        <Stack
          sx={theme => theme.internalMixins.columns({ gap: 2.5, justify: 'center' })}
          mb={2}
        >
          <CircleButton
            onClick={() => {
              setPolicy({ self: true })
              setActiveIndex(STEPS.RESIDENCE)
            }}
          >
            Yes
          </CircleButton>
          <CircleButton
            onClick={() => {
              setPolicy({ self: false })
              setActiveIndex(STEPS.RESIDENCE)
            }}
          >
            No
          </CircleButton>
        </Stack>
        <Type
          variant="dataMicro"
          color="neutralCool_700"
        >
          If you click ‘Yes’ you can still add other travelers to this policy.
        </Type>
      </Stack>

      {/* 2. RESIDENCE */}
      <Stack
        display="grid"
        p={3}
        textAlign="center"
      >
        <Label
          color="primaryHue"
          mb={2}
        >
          Residence
        </Label>
        <Type
          variant="nameplateSecondary"
          mb={3}
        >
          Where are {self ? 'you' : 'they'} living now?
        </Type>
        <Type
          color="neutralCool_700"
          mb={3}
        >
          This should be {self ? 'your' : 'their'} principal residence, defined as where {self ? 'you' : 'they'} reside at the time of purchasing this policy.
        </Type>
        <Stack
          gap={2}
          justifySelf="center"
          width="100%"
          maxWidth="375px"
          textAlign="left"
          mb={3}
        >
          <Input
            label="Country"
            value={country}
            readOnly
            endAdornment={<Box height="25px" width="20px"><Icon icon="Lock" /></Box>}
          />
          <StateSelect
            filter={(isMulti || is_multi) ? o => !['WY', 'NY'].includes(o) : o => !['WA', 'NH', 'NY'].includes(o)}
            addDC
            onChange={data => setPolicy({ state: data })}
          />
        </Stack>
        <Button
          justifyContent="center"
          disabled={!country?.length || !state?.length}
          onClick={() => {
            setActiveIndex(STEPS.TRAVELER_INFO)
          }}
        >
          Continue
        </Button>
        <Type
          variant="dataMicro"
          color="neutralCool_700"
          mt={3}
        >
          <strong>NOTE</strong>: All insureds listed on this plan must reside at the same State. If anyone resides at a different State, a separate quote must be obtained.
        </Type>
      </Stack>

      {/* 3. TRAVELER INFO */}
      <Stack
        display="grid"
        p={3}
        textAlign="center"
      >
        <Label
          color="primaryHue"
          mb={2}
        >
          Traveler Info
        </Label>
        <Type
          variant="nameplateSecondary"
          mb={3}
        >
          Tell us about who’s traveling
        </Type>
        <Stack
          display="grid"
          maxWidth={is_multi ? '450px' : undefined}
          width="100%"
          justifySelf="center"
          gap={3}
          textAlign="left"
          mb={3}
        >
          {travelers.map((o, idx) => (
            <Fragment
              key={`traveler_${idx}`}
            >
              <Stack
                gap={1.5}
              >
                <Stack
                  sx={theme => theme.internalMixins.columns({ gap: .5 })}
                >
                  {idx !== 0 && (
                    <IconButton
                      solid
                      onClick={() => removeTraveler(idx)}
                    >
                      <Icon
                        icon="Minus"
                        inheritTransition
                      />
                    </IconButton>
                  )}
                  <Type
                    variant="chipLabel"
                  >
                    {idx === 0 ? `${self ? 'Your' : 'Their'} information` : `Traveler #${idx + 1}`}
                  </Type>
                </Stack>
                <Stack
                  display="grid"
                  gap={1.5}
                  width="100%"
                  gridTemplateColumns={{
                    sm: is_multi ? undefined : 'repeat(2, 1fr)',
                  }}
                >
                  <Stack
                    gap={1}
                  >
                    <Label>
                      Date of Birth
                    </Label>
                    <DatePicker
                      openTo="year"
                      disableFuture
                      minDate={moment().subtract(85, 'years')}
                      maxDate={idx === 0 ? moment().subtract(18, 'years') : moment()}
                      outlined
                      value={o.dob}
                      onChange={data => {
                        setTraveler(idx, { 
                          dob: data?.format('YYYY-MM-DD'), 
                          age: moment().diff(data, 'years').toString(),
                        })
                      }}
                    />
                  </Stack>
                  {/* {!is_multi && (
                    <Input
                      label="Age"
                      type="number"
                      value={o.age}
                      onChange={data => setTraveler(idx, { age: data.toString() })}
                    />
                  )} */}
                  {!is_multi && (
                    <Input
                      label="Trip Cost"
                      mask="currency_no_prefix"
                      icon="$"
                      iconColor="mainCool"
                      removeMaskOnChange
                      value={o.individual_trip_cost}
                      onChange={data => setTraveler(idx, { individual_trip_cost: +data > 20000 ? '20000' : data })}
                    />
                  )}
                </Stack>
              </Stack>
              {idx !== travelers.length - 1 && <Divider color="neutralCool_300" />}
            </Fragment>
          ))}
          {travelers.length < 10 && (
            <IconAction
              icon="Add"
              variant="tertiary"
              onClick={addTraveler}
            >
              Add a traveler
            </IconAction>
          )}
        </Stack>
        <Button
          justifyContent="center"
          disabled={!(travelers.every(o => is_multi ? !!o.age?.length : (!!o.age?.length && !!+o.individual_trip_cost)))}
          onClick={() => {
            setActiveIndex(is_multi ? STEPS.TRAVEL_DATES : STEPS.DESTINATION)
          }}
        >
          Continue
        </Button>
      </Stack>

      {/* 4. DESTINATION */}
      <Stack
        p={3}
        textAlign="center"
      >
        <Label
          color="primaryHue"
          mb={2}
        >
          Destination
        </Label>
        <Type
          variant="nameplateSecondary"
          mb={3}
        >
          Where are {self ? 'you' : 'they'} traveling?
        </Type>
        <Type
          color="neutralCool_700"
          mb={2}
        >
          Add all countries {self ? 'you' : 'they'} plan on visiting this trip
        </Type>
        <Stack
          mb={3}
        >
          <DestinationCountries />
        </Stack>
        <Button
          justifyContent="center"
          disabled={!destinations.length}
          onClick={() => setActiveIndex(STEPS.FIRST_PAYMENT)}
        >
          Continue
        </Button>
      </Stack>

      {/* 5. FIRST PAYMENT */}
      <Stack
        p={3}
        textAlign="center"
      >
        <Label
          color="primaryHue"
          mb={2}
        >
          Travel Dates
        </Label>
        <Type
          variant="nameplateSecondary"
          mb={1}
        >

          When was the first payment made towards this trip?
        </Type>
        <Type
          variant="bodyTall"
          color="neutralCool_700"
          mb={3}
        >
          Select today’s date below if this is the first purchase for this trip.
        </Type>
        <Stack
          gap={1}
          textAlign="left"
          maxWidth="450px"
          width="100%"
          mx="auto"
          mb={3}
        >
          <Label>
            Date of First Payment
          </Label>
          <DatePicker
            disableFuture
            outlined
            value={deposit_date}
            onChange={data => {
              setPolicy({ deposit_date: data?.format('YYYY-MM-DD') })
            }}
          />
        </Stack>
        <Button
          justifyContent="center"
          disabled={!deposit_date?.length}
          onClick={() => {
            setActiveIndex(STEPS.TRAVEL_DATES)
          }}
        >
          Continue
        </Button>
      </Stack>

      {/* 6. TRAVEL DATES */}
      <Stack
        p={3}
        textAlign="center"
      >
        <Label
          color="primaryHue"
          mb={2}
        >
          Travel Dates
        </Label>
        {is_multi && (
          <>
            <Type
              variant="nameplateSecondary"
              mb={3}
            >

              {`Finally, when would you like ${self ? 'your' : 'their'} plan to begin?`}
            </Type>
            <Stack
              gap={1}
              textAlign="left"
              maxWidth="450px"
              width="100%"
              mx="auto"
              mb={3}
            >
              <Label>
                Select a Start Date
              </Label>
              <DatePicker
                disablePast
                outlined
                shouldDisableDate={shouldDisableDate}
                value={start_date}
                onChange={data => {
                  setPolicy({ start_date: data?.format('YYYY-MM-DD') })
                }}
              />
            </Stack>
            <Button
              justifyContent="center"
              disabled={!start_date?.length}
              onClick={() => {
                navigate('/shop-travel/trip-protection/multi-trip-overview')
                closeModal()
              }}
            >
              Get Your Quote
            </Button>
          </>
        )}
        {!is_multi && (
          <>
            <Type
              variant="nameplateSecondary"
              mb={3}
            >

              {`Finally, when are ${self ? 'you' : 'they'} traveling?`}
            </Type>
            <Stack
              gap={1}
              textAlign="left"
              mb={3}
            >
              <Label>
                Trip Dates
              </Label>
              <Stack
                gap={.5}
              >
                <DateRangePicker
                  disablePast
                  outlined
                  error={!!multiDateError}
                  value={[start_date, end_date]}
                  onChange={data => {
                    setPolicy({ 
                      start_date: data[0]?.format('YYYY-MM-DD') || null, 
                      end_date: data[1]?.format('YYYY-MM-DD') || null,
                    })
                  }}
                />
                {multiDateError && (
                  <Type
                    variant="dataMicro"
                    color="error"
                  >
                    {multiDateError}
                  </Type>
                )}
              </Stack>
            </Stack>
            <Button
              justifyContent="center"
              disabled={!start_date?.length || !end_date?.length || !!multiDateError}
              onClick={() => {
                navigate('/shop-travel/trip-protection/customize')
                closeModal()
              }}
            >
              Get Your Quote
            </Button>
          </>
        )}
        <Type
          variant="dataMicro"
          color="neutralCool_700"
          mt={6}
        >
          By continuing, you agree to the battleface <A href="https://www.battleface.com/en-us/privacy-policy/" wrapComponent><InlineButton>Privacy Policy</InlineButton></A>, <A href="https://www.battleface.com/en-us/terms-conditions/" wrapComponent><InlineButton>Terms & Conditions</InlineButton></A>, & <A href="https://www.battleface.com/en-gb/cookie-policy/" wrapComponent><InlineButton>Cookie Policy</InlineButton></A>.
        </Type>
        {is_multi && (
          <Box
            mt={3}
            mx="auto"
          >
            <Alert
              severity="error"
            >
              <Stack
                sx={theme => theme.internalMixins.columns({ gap: 1 })}
              >
                <div>
                  Multi-Trip Annual Plans don’t cover travel to certain destinations.
                </div>
                <TextButton
                  color="error"
                  onClick={() => setActiveIndex(STEPS.NON_COVERED_DESTINATIONS)}
                >
                  View list
                </TextButton>
              </Stack>
            </Alert>
          </Box>
        )}
      </Stack>

      {/* 7. NON-COVERED DESTINATIONS */}
      <Stack
        display="grid"
        pb={3}
        justifyContent="center"
      >
        <DestinationsNotCovered
          excludedDestinations={EXCLUDED_DESTINATIONS}
        />
        <Button
          outlined
          justifyContent="center"
          onClick={() => setActiveIndex(STEPS.TRAVEL_DATES)}
        >
          Back
        </Button>
      </Stack>
    </ModalFlow>
  )
}

AddTripProtection.propTypes = {
  vendorID: PropTypes.string,
  productID: PropTypes.string,
  isMulti: PropTypes.bool,
  tripID: PropTypes.string,
}

export default AddTripProtection